'use client';

import React, { useEffect, useState, useRef } from 'react';

import { useAuth } from '@/contexts/AuthContext';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { CookieSettingsLink as CookieSettingsLinkLanding } from '@/components/CookieBanner';

// ── SVG Icons ─────────────────────────────────────────────────────────────────
const CommandIcon = ({ size = 24 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
    <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
  </svg>
);
const ShoppingCartIcon = ({ size = 24 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
  </svg>
);
const FinanceIcon = ({ size = 24 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
  </svg>
);
const BoxIcon = ({ size = 24 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
  </svg>
);
const UsersIcon = ({ size = 24 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);
const SparkIcon = ({ size = 24 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L9.5 9.5 2 12l7.5 2.5L12 22l2.5-7.5L22 12l-7.5-2.5z"/>
  </svg>
);
const ArrowRightIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
);
const ChevronRightIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
const TrendUpIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
  </svg>
);
const AlertIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);
const CheckIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const PlayIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <polygon points="5 3 19 12 5 21 5 3"/>
  </svg>
);
const XIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

// ── Mini Sparkline Chart ──────────────────────────────────────────────────────
function MiniSparkline({ data, color, height = 40 }: { data: number[]; color: string; height?: number }) {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const w = 120;
  const h = height;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - ((v - min) / range) * (h - 4) - 2;
    return `${x},${y}`;
  });
  const polyline = pts.join(' ');
  const area = `0,${h} ${polyline} ${w},${h}`;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} fill="none">
      <defs>
        <linearGradient id={`sg-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35"/>
          <stop offset="100%" stopColor={color} stopOpacity="0"/>
        </linearGradient>
      </defs>
      <polygon points={area} fill={`url(#sg-${color.replace('#', '')})`}/>
      <polyline points={polyline} stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// ── Mini Bar Chart ────────────────────────────────────────────────────────────
function MiniBarChart({ data, color }: { data: number[]; color: string }) {
  const max = Math.max(...data);
  const w = 120;
  const h = 40;
  const barW = (w / data.length) * 0.6;
  const gap = w / data.length;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} fill="none">
      {data.map((v, i) => {
        const barH = (v / max) * (h - 4);
        const x = i * gap + gap * 0.2;
        const y = h - barH;
        return (
          <rect key={i} x={x} y={y} width={barW} height={barH} rx="2"
            fill={color} opacity={i === data.length - 1 ? 1 : 0.45}/>
        );
      })}
    </svg>
  );
}

// ── Module Data ───────────────────────────────────────────────────────────────
interface ModuleCard {
  id: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  desc: string;
  color: string;
  gradient: string;
  route: string;
  kpis: { label: string; value: string; delta?: string; positive?: boolean }[];
  chartType: 'bar' | 'line' | 'none';
  chartData: number[];
  chartLabel?: string;
  preview: React.ReactNode;
}

const MODULES: ModuleCard[] = [
  {
    id: 'centro-mando',
    icon: <CommandIcon size={22} />,
    title: 'Centro de Mando',
    subtitle: 'Toda la información de tu negocio en tiempo real, en un solo lugar.',
    desc: 'Panel ejecutivo con KPIs críticos, flujo de caja en tiempo real, alertas automáticas y visión completa de tu operación. Toma decisiones con datos, no con intuición.',
    color: '#7c6ff7',
    gradient: 'linear-gradient(135deg, #7c6ff7 0%, #4f46e5 100%)',
    route: '/admin-vision/centro-mando',
    kpis: [
      { label: 'Ventas del mes', value: '€ 42,850', delta: '+12.4%', positive: true },
      { label: 'Utilidad neta', value: '€ 8,420', delta: '+8.7%', positive: true },
      { label: 'Caja disponible', value: '€ 18,250', delta: '', positive: true },
      { label: 'CxC vencidas', value: '3 facturas', delta: 'Atención', positive: false },
    ],
    chartType: 'bar',
    chartData: [28, 35, 30, 42, 38, 45, 43],
    chartLabel: 'Ventas últimos 7 días',
    preview: (
      <div className="flex items-end gap-1">
        {[28,35,30,42,38,45,43].map((v,i) => (
          <div key={i} className="rounded-sm flex-1" style={{ height: `${(v/45)*32}px`, background: i===6 ? '#7c6ff7' : 'rgba(124,111,247,0.35)' }}/>
        ))}
      </div>
    ),
  },
  {
    id: 'ventas-pos',
    icon: <ShoppingCartIcon size={22} />,
    title: 'Ventas y POS',
    subtitle: 'Vende, cobra y controla desde cualquier dispositivo.',
    desc: 'Punto de venta rápido, registro de ventas, cotizaciones profesionales y control de cobranza. Integrado con inventario y clientes para una operación sin fricciones.',
    color: '#3b82f6',
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
    route: '/admin-vision/ventas',
    kpis: [
      { label: 'Ventas hoy', value: '€ 2,850', delta: '+12.4%', positive: true },
      { label: 'Ticket promedio', value: '€ 142', delta: '+5.2%', positive: true },
      { label: 'Cotizaciones', value: '8 activas', delta: '', positive: true },
      { label: 'Cobros pendientes', value: '€ 4,280', delta: 'Revisar', positive: false },
    ],
    chartType: 'bar',
    chartData: [18, 22, 19, 28, 24, 31, 29],
    chartLabel: 'Ventas diarias',
    preview: (
      <div className="flex items-center gap-2 text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(59,130,246,0.25)' }}>
          <ShoppingCartIcon size={14} />
        </div>
        <div>
          <div className="font-semibold text-white">Café Espresso</div>
          <div>€ 2.50 · Stock: 48</div>
        </div>
      </div>
    ),
  },
  {
    id: 'finanzas',
    icon: <FinanceIcon size={22} />,
    title: 'Finanzas',
    subtitle: 'Controla ingresos, gastos y flujo de caja.',
    desc: 'Gestión completa de cuentas por cobrar, cuentas por pagar, gastos y flujo de efectivo. Nunca pierdas de vista tu liquidez ni te sorprendas con deudas vencidas.',
    color: '#10b981',
    gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    route: '/admin-vision/cuentas-por-cobrar',
    kpis: [
      { label: 'Flujo de caja', value: '€ 18,250', delta: '+3.1%', positive: true },
      { label: 'CxC pendiente', value: '€ 4,280', delta: '3 vencidas', positive: false },
      { label: 'CxP este mes', value: '€ 6,100', delta: 'Al día', positive: true },
      { label: 'Gastos fijos', value: '€ 3,200', delta: 'Normal', positive: true },
    ],
    chartType: 'line',
    chartData: [12, 15, 13, 18, 16, 19, 18],
    chartLabel: 'Flujo de caja (miles €)',
    preview: (
      <div className="flex items-center gap-1.5 text-xs" style={{ color: 'rgba(255,255,255,0.8)' }}>
        <TrendUpIcon size={14} />
        <span className="font-semibold text-white">€ 18,250</span>
        <span>flujo disponible</span>
      </div>
    ),
  },
  {
    id: 'inventario',
    icon: <BoxIcon size={22} />,
    title: 'Inventario',
    subtitle: 'Productos, stock y alertas automáticas.',
    desc: 'Control de productos, niveles de stock, costos y alertas de mínimos. Trazabilidad completa de entradas y salidas para que nunca te quedes sin lo que vendes.',
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    route: '/admin-vision/productos',
    kpis: [
      { label: 'Productos activos', value: '148', delta: '', positive: true },
      { label: 'Stock bajo', value: '7 alertas', delta: 'Revisar', positive: false },
      { label: 'Valor inventario', value: '€ 24,600', delta: '', positive: true },
      { label: 'Rotación', value: '18 días', delta: 'Óptimo', positive: true },
    ],
    chartType: 'bar',
    chartData: [45, 38, 52, 41, 48, 35, 42],
    chartLabel: 'Movimientos de stock',
    preview: (
      <div className="flex items-center gap-2 text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(245,158,11,0.25)' }}>
          <BoxIcon size={14} />
        </div>
        <div>
          <div className="font-semibold text-white">Pastel Red Velvet</div>
          <div className="flex items-center gap-1"><AlertIcon size={10}/> Stock: 12 — bajo</div>
        </div>
      </div>
    ),
  },
  {
    id: 'clientes',
    icon: <UsersIcon size={22} />,
    title: 'Clientes',
    subtitle: 'Historial completo y más ventas por cliente.',
    desc: 'CRM integrado con historial de compras, seguimiento de cobranza, notas y recordatorios. Conoce a tus mejores clientes y actúa antes de perderlos.',
    color: '#ec4899',
    gradient: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
    route: '/admin-vision/clientes',
    kpis: [
      { label: 'Clientes activos', value: '284', delta: '+8 este mes', positive: true },
      { label: 'Ticket promedio', value: '€ 298', delta: '+5.2%', positive: true },
      { label: 'Seguimientos hoy', value: '5 pendientes', delta: '', positive: false },
      { label: 'Retención', value: '87%', delta: '+2%', positive: true },
    ],
    chartType: 'line',
    chartData: [22, 25, 23, 28, 26, 30, 28],
    chartLabel: 'Clientes nuevos por semana',
    preview: (
      <div className="flex items-center gap-2 text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
        <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm" style={{ background: 'rgba(236,72,153,0.35)', color: '#ec4899' }}>D</div>
        <div>
          <div className="font-semibold text-white">Daniela</div>
          <div>5 compras · € 298.00</div>
        </div>
      </div>
    ),
  },
  {
    id: 'eva',
    icon: <SparkIcon size={22} />,
    title: 'EVA — IA',
    subtitle: 'Tu administradora inteligente que te guía y da acción.',
    desc: 'EVA analiza tu negocio en tiempo real, detecta riesgos, identifica oportunidades y te da recomendaciones concretas. No solo datos — decisiones.',
    color: '#a78bfa',
    gradient: 'linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)',
    route: '/admin-vision/eva-decisiones',
    kpis: [
      { label: 'Recomendaciones', value: '3 activas', delta: 'Hoy', positive: true },
      { label: 'Riesgo detectado', value: 'Flujo de caja', delta: '12 días', positive: false },
      { label: 'Oportunidad', value: 'Ticket medio', delta: '+18% posible', positive: true },
      { label: 'Alertas EVA', value: '2 críticas', delta: 'Revisar', positive: false },
    ],
    chartType: 'none',
    chartData: [],
    preview: (
      <div className="flex items-center gap-2 text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(167,139,250,0.25)' }}>
          <SparkIcon size={14} />
        </div>
        <div>
          <div className="font-semibold text-white">EVA</div>
          <div>Tu flujo de caja podría estar en riesgo en 12 días.</div>
        </div>
      </div>
    ),
  },
];

// ── EVA Recommendations ───────────────────────────────────────────────────────
const EVA_RECS = [
  { num: 1, text: 'Revisar 3 facturas vencidas', color: '#f59e0b' },
  { num: 2, text: 'Oportunidad de aumentar ticket medio', color: '#10b981' },
  { num: 3, text: 'Buen desempeño en ventas esta semana', color: '#a78bfa' },
];

// ── Dashboard Mockup ──────────────────────────────────────────────────────────
function DashboardMockup() {
  return (
    <div
      className="relative rounded-2xl overflow-hidden w-full"
      style={{
        background: 'rgba(10,8,30,0.95)',
        border: '1px solid rgba(124,111,247,0.25)',
        boxShadow: '0 0 60px rgba(124,111,247,0.18), 0 0 120px rgba(6,182,212,0.08)',
        backdropFilter: 'blur(20px)',
      }}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-2.5" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}>
        <div className="flex items-center gap-2">
          <img
            src="/assets/images/LOGOTIPO_Administracion_VisionarIA_PNG_2-1789771521659.png"
            alt="Administración VisionarIA"
            width={22}
            height={22}
            style={{ objectFit: 'contain', filter: 'brightness(1.1)' }}
          />
          <span className="text-xs font-semibold" style={{ color: 'rgba(255,255,255,0.9)' }}>Administración VisionarIA</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)' }}>Hoy</div>
          <div className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>13 Sep 2026</div>
        </div>
      </div>

      <div className="flex" style={{ minHeight: 280 }}>
        {/* Sidebar */}
        <div className="w-28 shrink-0 py-3 px-2 flex flex-col gap-0.5" style={{ borderRight: '1px solid rgba(255,255,255,0.05)' }}>
          {['Inicio','Centro de Mando','Ventas / POS','Inventario','Clientes','CxC','CxP','Gastos','Reportes','EVA'].map((item, i) => (
            <div key={item} className="flex items-center gap-1.5 px-2 py-1 rounded-md" style={{
              background: i === 0 ? 'rgba(124,111,247,0.18)' : 'transparent',
              color: i === 0 ? '#a78bfa' : 'rgba(255,255,255,0.38)',
              fontSize: 9,
            }}>
              <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: i === 0 ? '#7c6ff7' : 'rgba(255,255,255,0.15)' }}/>
              {item}
            </div>
          ))}
        </div>

        {/* Main content */}
        <div className="flex-1 p-3 flex flex-col gap-2.5 overflow-hidden">
          {/* Greeting */}
          <div>
            <div className="text-xs font-semibold" style={{ color: 'rgba(255,255,255,0.9)' }}>Hola, Manuel 👋</div>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)' }}>Aquí tienes el resumen de tu negocio hoy.</div>
          </div>

          {/* KPI row */}
          <div className="grid grid-cols-4 gap-1.5">
            {[
              { label: 'Ventas del día', val: '€ 2,850', delta: '↑ 12.4%', color: '#4f9cf9' },
              { label: 'Utilidad', val: '€ 842', delta: '↑ 8.7%', color: '#10b981' },
              { label: 'Caja disponible', val: '€ 18,250', delta: '', color: '#a78bfa' },
              { label: 'Cuentas por cobrar', val: '€ 4,280', delta: '3 vencidas', color: '#f59e0b', alert: true },
            ].map((kpi) => (
              <div key={kpi.label} className="rounded-lg p-2" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.4)', marginBottom: 2 }}>{kpi.label}</div>
                <div className="font-bold" style={{ fontSize: 11, color: 'rgba(255,255,255,0.92)' }}>{kpi.val}</div>
                {kpi.delta && (
                  <div style={{ fontSize: 8, color: kpi.alert ? '#f59e0b' : kpi.color }}>{kpi.delta}</div>
                )}
              </div>
            ))}
          </div>

          {/* Chart + EVA row */}
          <div className="grid grid-cols-5 gap-2 flex-1">
            {/* Chart */}
            <div className="col-span-3 rounded-lg p-2.5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
              <div className="flex items-center justify-between mb-2">
                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>Ventas de los últimos 30 días</div>
                <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.3)' }}>Últimos 30 días</div>
              </div>
              <div className="flex items-end gap-0.5 h-16">
                {[8,12,10,15,11,18,14,20,16,22,19,25,21,28,24,30,26,32,28,35,30,38,34,40,36,42,38,44,40,43].map((v,i) => (
                  <div key={i} className="flex-1 rounded-sm" style={{
                    height: `${(v/44)*100}%`,
                    background: i >= 25 ? 'linear-gradient(to top, #7c6ff7, #a78bfa)' : 'rgba(124,111,247,0.3)',
                    minWidth: 2,
                  }}/>
                ))}
              </div>
              <div className="flex justify-between mt-1" style={{ fontSize: 7, color: 'rgba(255,255,255,0.25)' }}>
                <span>1 Sep</span><span>8 Sep</span><span>15 Sep</span><span>22 Sep</span><span>30 Sep</span>
              </div>
            </div>

            {/* EVA panel */}
            <div className="col-span-2 rounded-lg p-2.5 flex flex-col gap-1.5" style={{ background: 'rgba(124,111,247,0.08)', border: '1px solid rgba(124,111,247,0.2)' }}>
              <div className="flex items-center gap-1.5">
                <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#7c6ff7,#a78bfa)' }}>
                  <SparkIcon size={10} />
                </div>
                <div>
                  <div style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.9)' }}>EVA</div>
                  <div style={{ fontSize: 7, color: 'rgba(167,139,250,0.8)' }}>Tu administradora inteligente</div>
                </div>
              </div>
              {EVA_RECS.map((r) => (
                <div key={r.num} className="flex items-start gap-1.5">
                  <div className="w-3.5 h-3.5 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: r.color, fontSize: 7, color: 'white', fontWeight: 700 }}>{r.num}</div>
                  <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.65)', lineHeight: 1.3 }}>{r.text}</div>
                </div>
              ))}
              <div className="mt-auto pt-1">
                <div className="text-center py-1 rounded-md" style={{ background: 'rgba(124,111,247,0.25)', fontSize: 8, color: '#a78bfa', fontWeight: 600 }}>
                  Ver recomendaciones →
                </div>
              </div>
            </div>
          </div>

          {/* Quick actions */}
          <div>
            <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.35)', marginBottom: 4 }}>Accesos rápidos</div>
            <div className="flex gap-1.5">
              {['Nueva venta','Registrar gasto','Nuevo cliente','Agregar producto'].map((a, i) => (
                <div key={a} className="flex-1 py-1.5 rounded-lg text-center" style={{
                  background: ['rgba(124,111,247,0.2)','rgba(239,68,68,0.15)','rgba(236,72,153,0.15)','rgba(245,158,11,0.15)'][i],
                  fontSize: 8,
                  color: ['#a78bfa','#f87171','#f472b6','#fbbf24'][i],
                  fontWeight: 600,
                }}>
                  {a}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Mobile Mockup ─────────────────────────────────────────────────────────────
function MobileMockup() {
  return (
    <div
      className="relative rounded-3xl overflow-hidden"
      style={{
        width: 140,
        background: 'rgba(10,8,30,0.98)',
        border: '1.5px solid rgba(124,111,247,0.3)',
        boxShadow: '0 0 30px rgba(124,111,247,0.2)',
      }}
    >
      {/* Status bar */}
      <div className="flex items-center justify-between px-3 py-1.5" style={{ background: 'rgba(255,255,255,0.03)' }}>
        <span style={{ fontSize: 7, color: 'rgba(255,255,255,0.5)' }}>9:41</span>
        <div className="flex gap-0.5">
          {[1,2,3].map(i => <div key={i} className="rounded-sm" style={{ width: 3, height: i*2+2, background: 'rgba(255,255,255,0.5)' }}/>)}
        </div>
      </div>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <div className="flex items-center gap-1">
          <img
            src="/assets/images/LOGOTIPO_Administracion_VisionarIA_PNG_2-1789771521659.png"
            alt="Administración VisionarIA"
            width={16}
            height={16}
            style={{ objectFit: 'contain', filter: 'brightness(1.1)' }}
          />
          <span style={{ fontSize: 8, color: 'rgba(255,255,255,0.8)', fontWeight: 600 }}>Admin VisionarIA</span>
        </div>
        <SparkIcon size={10} />
      </div>
      {/* Content */}
      <div className="px-3 py-2 flex flex-col gap-2">
        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.9)', fontWeight: 700 }}>Hola, Manuel</div>
        <div className="flex gap-1">
          {['Hoy','Semana','Mes'].map((t,i) => (
            <div key={t} className="flex-1 text-center py-0.5 rounded" style={{
              background: i===0 ? 'rgba(124,111,247,0.3)' : 'rgba(255,255,255,0.05)',
              fontSize: 7,
              color: i===0 ? '#a78bfa' : 'rgba(255,255,255,0.4)',
            }}>{t}</div>
          ))}
        </div>
        <div className="rounded-lg p-2" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ fontSize: 7, color: 'rgba(255,255,255,0.4)' }}>Ventas</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'white' }}>€ 2,850</div>
          <div style={{ fontSize: 7, color: '#10b981' }}>↑ 12.4%</div>
          <div className="flex items-end gap-0.5 mt-1.5 h-8">
            {[6,9,7,11,8,13,11].map((v,i) => (
              <div key={i} className="flex-1 rounded-sm" style={{ height: `${(v/13)*100}%`, background: i===6 ? '#7c6ff7' : 'rgba(124,111,247,0.3)' }}/>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          {[
            { color: '#f59e0b', text: '3 tareas de EVA' },
            { color: '#4f9cf9', text: '2 cobros pendientes' },
            { color: '#f472b6', text: '5 productos con bajo stock' },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: item.color }}/>
              <span style={{ fontSize: 7, color: 'rgba(255,255,255,0.6)' }}>{item.text}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Bottom nav */}
      <div className="flex items-center justify-around px-2 py-2" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        {['Inicio','Ventas','EVA','Más'].map((item, i) => (
          <div key={item} className="flex flex-col items-center gap-0.5">
            <div className="w-3.5 h-3.5 rounded" style={{ background: i===0 ? 'rgba(124,111,247,0.3)' : 'transparent' }}/>
            <span style={{ fontSize: 6, color: i===0 ? '#a78bfa' : 'rgba(255,255,255,0.35)' }}>{item}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Expanded Panel ────────────────────────────────────────────────────────────
interface ExpandedPanelProps {
  module: ModuleCard;
  onClose: () => void;
}

function ExpandedPanel({ module, onClose }: ExpandedPanelProps) {
  return (
    <div
      className="col-span-full rounded-2xl overflow-hidden"
      style={{
        background: 'rgba(10,8,30,0.97)',
        border: `1px solid ${module.color}40`,
        boxShadow: `0 0 60px ${module.color}20, 0 24px 80px rgba(0,0,0,0.6)`,
        animation: 'expandPanel 0.3s cubic-bezier(0.34,1.56,0.64,1)',
      }}
    >
      <div className="p-6 md:p-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: module.gradient, boxShadow: `0 8px 24px ${module.color}40` }}>
              <span style={{ color: 'white' }}>{module.icon}</span>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-1" style={{ color: 'white' }}>{module.title}</h3>
              <p className="text-sm" style={{ color: module.color }}>{module.subtitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:opacity-80"
            style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)' }}
          >
            <XIcon size={14} />
          </button>
        </div>

        {/* Description */}
        <p className="text-sm leading-relaxed mb-6 max-w-2xl" style={{ color: 'rgba(255,255,255,0.65)' }}>
          {module.desc}
        </p>

        {/* KPIs + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* KPIs */}
          <div className="md:col-span-2 grid grid-cols-2 gap-3">
            {module.kpis.map((kpi) => (
              <div key={kpi.label} className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="text-xs mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>{kpi.label}</div>
                <div className="text-lg font-bold mb-1" style={{ color: 'white' }}>{kpi.value}</div>
                {kpi.delta && (
                  <div className="flex items-center gap-1 text-xs" style={{ color: kpi.positive ? '#10b981' : '#f59e0b' }}>
                    {kpi.positive ? <TrendUpIcon size={11}/> : <AlertIcon size={11}/>}
                    {kpi.delta}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Chart or EVA recs */}
          <div className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
            {module.id === 'eva' ? (
              <div className="flex flex-col gap-3 h-full">
                <div className="text-xs font-semibold mb-1" style={{ color: 'rgba(255,255,255,0.6)' }}>Recomendaciones activas</div>
                {EVA_RECS.map((r) => (
                  <div key={r.num} className="flex items-start gap-2.5 p-2.5 rounded-lg" style={{ background: `${r.color}12`, border: `1px solid ${r.color}25` }}>
                    <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 text-xs font-bold" style={{ background: r.color, color: 'white' }}>{r.num}</div>
                    <span className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.75)' }}>{r.text}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col h-full">
                <div className="text-xs font-semibold mb-3" style={{ color: 'rgba(255,255,255,0.5)' }}>{module.chartLabel}</div>
                <div className="flex-1 flex items-end">
                  {module.chartType === 'bar' && (
                    <MiniBarChart data={module.chartData} color={module.color} />
                  )}
                  {module.chartType === 'line' && (
                    <MiniSparkline data={module.chartData} color={module.color} />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* CTA */}
        <div className="flex items-center gap-3">
          <Link
            href={module.route}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold transition-all hover:opacity-90 hover:scale-[1.02]"
            style={{ background: module.gradient, color: 'white', boxShadow: `0 8px 24px ${module.color}35` }}
          >
            Entrar al módulo
            <ArrowRightIcon size={14} />
          </Link>
          <span className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>Acceso directo al módulo completo</span>
        </div>
      </div>
    </div>
  );
}

// ── Module Card ───────────────────────────────────────────────────────────────
interface ModuleCardProps {
  module: ModuleCard;
  isActive: boolean;
  isDimmed: boolean;
  onClick: () => void;
}

function ModuleCardComponent({ module, isActive, isDimmed, onClick }: ModuleCardProps) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="relative rounded-2xl cursor-pointer select-none"
      style={{
        background: isActive
          ? `linear-gradient(145deg, ${module.color}18 0%, rgba(10,8,30,0.95) 100%)`
          : 'rgba(255,255,255,0.03)',
        border: `1px solid ${isActive ? module.color + '50' : hovered ? module.color + '30' : 'rgba(255,255,255,0.07)'}`,
        boxShadow: isActive
          ? `0 0 30px ${module.color}25, 0 8px 32px rgba(0,0,0,0.4)`
          : hovered
          ? `0 0 20px ${module.color}18, 0 4px 20px rgba(0,0,0,0.3)`
          : '0 2px 8px rgba(0,0,0,0.2)',
        transform: hovered && !isActive ? 'translateY(-3px) scale(1.01)' : isActive ? 'translateY(-2px)' : 'translateY(0)',
        opacity: isDimmed ? 0.45 : 1,
        transition: 'all 0.28s cubic-bezier(0.4,0,0.2,1)',
      }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Glow accent top */}
      {(hovered || isActive) && (
        <div
          className="absolute top-0 left-0 right-0 h-px rounded-t-2xl"
          style={{ background: `linear-gradient(90deg, transparent, ${module.color}80, transparent)` }}
        />
      )}

      <div className="p-5">
        {/* Icon */}
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
          style={{
            background: isActive ? module.gradient : `${module.color}18`,
            color: isActive ? 'white' : module.color,
            boxShadow: isActive ? `0 6px 20px ${module.color}35` : 'none',
            transition: 'all 0.28s ease',
          }}
        >
          {module.icon}
        </div>

        {/* Title */}
        <h3 className="font-bold text-sm mb-1.5" style={{ color: isActive ? 'white' : 'rgba(255,255,255,0.88)' }}>
          {module.title}
        </h3>

        {/* Subtitle */}
        <p className="text-xs leading-relaxed mb-4" style={{ color: 'rgba(255,255,255,0.45)', lineHeight: 1.5 }}>
          {module.subtitle}
        </p>

        {/* Preview */}
        <div className="mt-auto">
          {module.preview}
        </div>

        {/* Arrow */}
        <div
          className="absolute bottom-4 right-4 w-7 h-7 rounded-full flex items-center justify-center"
          style={{
            background: isActive ? module.gradient : 'rgba(255,255,255,0.06)',
            color: isActive ? 'white' : module.color,
            transition: 'all 0.28s ease',
          }}
        >
          <ChevronRightIcon size={12} />
        </div>
      </div>
    </div>
  );
}

// ── Main Landing Page ─────────────────────────────────────────────────────────
export default function LandingPage() {
  const { user, loading, permissions, signOut } = useAuth();
  const [mounted, setMounted] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [activeModule, setActiveModule] = useState<string | null>(null);
  const expandRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setMounted(true); }, []);

  const handleSignOut = async () => {
    setSigningOut(true);
    try { await signOut(); } catch { /* ignore */ } finally { setSigningOut(false); }
  };

  const getDashboardHref = () => {
    if (permissions?.isMaster) return '/admin-master';
    if (permissions?.companyId) return '/dashboard';
    return '/onboarding';
  };

  const handleCardClick = (id: string) => {
    const next = activeModule === id ? null : id;
    setActiveModule(next);
    if (next && expandRef.current) {
      setTimeout(() => expandRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
    }
  };

  if (loading && !mounted) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#08061a' }}>
        <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#7c6ff7' }} />
      </div>
    );
  }

  const isAuthenticated = mounted && !loading && !!user;

  // Find active module index for expand panel placement
  const activeIndex = MODULES.findIndex(m => m.id === activeModule);

  // Build rows of 3 for desktop (to insert expand panel after the row containing active card)
  const rowSize = 3;
  const rows: ModuleCard[][] = [];
  for (let i = 0; i < MODULES.length; i += rowSize) {
    rows.push(MODULES.slice(i, i + rowSize));
  }
  const activeRowIndex = activeIndex >= 0 ? Math.floor(activeIndex / rowSize) : -1;

  return (
    <div className="min-h-screen" style={{ background: '#08061a', color: 'white' }}>
      {/* Keyframes */}
      <style>{`
        @keyframes expandPanel {
          from { opacity: 0; transform: scaleY(0.92); }
          to { opacity: 1; transform: scaleY(1); }
        }
        @keyframes floatUp {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-6px); }
        }
        @keyframes pulseGlow {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>

      {/* ── Ambient BG ── */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute top-[-10%] left-[20%] w-[600px] h-[600px] rounded-full blur-[140px]" style={{ background: 'radial-gradient(circle, rgba(124,111,247,0.12), transparent 70%)' }}/>
        <div className="absolute top-[30%] right-[10%] w-[400px] h-[400px] rounded-full blur-[120px]" style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.07), transparent 70%)' }}/>
        <div className="absolute bottom-[10%] left-[5%] w-[500px] h-[500px] rounded-full blur-[130px]" style={{ background: 'radial-gradient(circle, rgba(79,156,249,0.06), transparent 70%)' }}/>
      </div>

      {/* ── Navbar ── */}
      <nav className="relative z-20 flex items-center justify-between px-6 py-4 max-w-7xl mx-auto">
        <div className="flex items-center gap-3">
          <AppLogo size={40} />
          <div className="flex flex-col leading-tight">
            <span className="font-black text-sm tracking-tight text-white">Administración</span>
            <span className="font-black text-sm tracking-tight" style={{ background: 'linear-gradient(90deg, #a78bfa, #4f9cf9)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>VisionarIA</span>
          </div>
        </div>

        {/* Nav links — desktop */}
        <div className="hidden md:flex items-center gap-6">
          {['Producto','Soluciones','Precios','Recursos','Contacto'].map(item => (
            <span key={item} className="text-sm cursor-pointer transition-colors hover:text-white" style={{ color: 'rgba(255,255,255,0.55)' }}>{item}</span>
          ))}
        </div>

        {/* Auth buttons */}
        <div className="flex items-center gap-2">
          {isAuthenticated ? (
            <>
              <Link href={getDashboardHref()} className="text-sm font-medium px-4 py-2 rounded-lg transition-colors" style={{ color: 'rgba(255,255,255,0.7)' }}>
                Ir a mi dashboard
              </Link>
              <button onClick={handleSignOut} disabled={signingOut} className="text-sm font-medium px-4 py-2 rounded-lg transition-colors disabled:opacity-50" style={{ color: 'rgba(255,255,255,0.45)' }}>
                {signingOut ? 'Saliendo…' : 'Cerrar sesión'}
              </button>
            </>
          ) : (
            <>
              <Link href="/login" className="text-sm font-medium px-4 py-2 rounded-lg border transition-all hover:border-white/20" style={{ color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.12)' }}>
                Iniciar sesión
              </Link>
              <Link href="/registro" className="text-sm font-semibold px-5 py-2 rounded-lg transition-all hover:opacity-90 hover:scale-[1.02]" style={{ background: 'linear-gradient(135deg, #7c6ff7, #4f46e5)', color: 'white', boxShadow: '0 4px 16px rgba(124,111,247,0.4)' }}>
                Probar gratis →
              </Link>
            </>
          )}
        </div>
      </nav>

      {/* ── Hero Section ── */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pt-8 pb-16">
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">

          {/* LEFT — Text */}
          <div className="flex-1 max-w-lg">
            {/* Brand badge with logo */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5" style={{ background: 'rgba(124,111,247,0.1)', border: '1px solid rgba(124,111,247,0.25)' }}>
              <AppLogo size={20} />
              <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: 'rgba(255,255,255,0.6)', letterSpacing: '0.1em' }}>Administración VisionarIA</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black leading-[1.05] mb-4 tracking-tight">
              <span className="text-white">Entiende tu negocio.</span>
              <br />
              <span style={{ background: 'linear-gradient(135deg, #7c6ff7 0%, #4f9cf9 50%, #06b6d4 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Decide antes.
              </span>
            </h1>
            <p className="text-sm leading-relaxed mb-8" style={{ color: 'rgba(255,255,255,0.55)', maxWidth: 380 }}>
              Admin con Visión combina tus ventas, finanzas, inventario y clientes con la inteligencia de EVA para darte claridad, control y crecimiento real.
            </p>

            <div className="flex flex-col sm:flex-row items-start gap-3 mb-6">
              {isAuthenticated ? (
                <>
                  <Link href={getDashboardHref()} className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-all hover:opacity-90 hover:scale-[1.02]" style={{ background: 'linear-gradient(135deg, #7c6ff7, #4f46e5)', color: 'white', boxShadow: '0 8px 28px rgba(124,111,247,0.4)' }}>
                    Ir a mi dashboard <ArrowRightIcon />
                  </Link>
                  <button onClick={handleSignOut} disabled={signingOut} className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-medium transition-all hover:opacity-80 disabled:opacity-50" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.8)' }}>
                    {signingOut ? 'Cerrando…' : 'Cerrar sesión'}
                  </button>
                </>
              ) : (
                <>
                  <Link href="/registro" className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-all hover:opacity-90 hover:scale-[1.02]" style={{ background: 'linear-gradient(135deg, #7c6ff7, #4f46e5)', color: 'white', boxShadow: '0 8px 28px rgba(124,111,247,0.4)' }}>
                    Probar gratis <ArrowRightIcon />
                  </Link>
                  <Link href="/login" className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-medium transition-all hover:opacity-80" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.8)' }}>
                    <PlayIcon size={12} /> Ver video (2 min)
                  </Link>
                </>
              )}
            </div>

            <div className="flex items-center gap-4 flex-wrap">
              {['Sin tarjeta de crédito','Configura en minutos','Funciona en web y móvil'].map(item => (
                <div key={item} className="flex items-center gap-1.5 text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>
                  <CheckIcon size={12} />
                  {item}
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — Dashboard mockup */}
          <div className="flex-1 relative w-full max-w-2xl" style={{ animation: 'floatUp 6s ease-in-out infinite' }}>
            {/* Outer glow */}
            <div className="absolute -inset-6 rounded-3xl blur-3xl pointer-events-none" style={{ background: 'linear-gradient(135deg, rgba(124,111,247,0.15), rgba(6,182,212,0.08))', zIndex: 0 }} aria-hidden="true"/>

            <div className="relative z-10 flex items-start gap-3">
              {/* Main dashboard */}
              <div className="flex-1">
                <DashboardMockup />
              </div>

              {/* Mobile mockup — floating right */}
              <div className="hidden lg:flex flex-col items-end gap-2 shrink-0" style={{ marginTop: 20 }}>
                <div className="text-right mb-1">
                  <div className="text-xs font-bold italic" style={{ color: 'rgba(255,255,255,0.5)' }}>Más visión</div>
                  <div className="text-xs font-bold italic" style={{ color: 'rgba(255,255,255,0.5)' }}>Más libertad</div>
                </div>
                <MobileMockup />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 6 Interactive Module Cards ── */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pb-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((row, rowIdx) => (
            <React.Fragment key={rowIdx}>
              {/* Cards in this row */}
              {row.map((mod) => (
                <ModuleCardComponent
                  key={mod.id}
                  module={mod}
                  isActive={activeModule === mod.id}
                  isDimmed={activeModule !== null && activeModule !== mod.id}
                  onClick={() => handleCardClick(mod.id)}
                />
              ))}

              {/* Expand panel after the row containing the active card */}
              {activeModule && activeRowIndex === rowIdx && (
                <div ref={expandRef} className="col-span-1 sm:col-span-2 lg:col-span-3">
                  <ExpandedPanel
                    module={MODULES.find(m => m.id === activeModule)!}
                    onClose={() => setActiveModule(null)}
                  />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* ── Footer bar ── */}
      <footer className="relative z-10 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <AppLogo size={36} />
              <div className="flex flex-col leading-tight">
                <span className="text-sm font-black text-white">Administración</span>
                <span className="text-sm font-black" style={{ background: 'linear-gradient(90deg, #a78bfa, #4f9cf9)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>VisionarIA</span>
              </div>
              <span className="text-xs ml-2 hidden md:block" style={{ color: 'rgba(255,255,255,0.3)' }}>Más de 1,000 negocios ya confían en Admin con Visión</span>
            </div>

            <div className="flex items-center gap-6 flex-wrap justify-center">
              {['Talleres','Restaurantes','Inmobiliarias','Comercios','Servicios','Instaladores'].map(item => (
                <span key={item} className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>{item}</span>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <div className="flex gap-0.5">
                {[1,2,3,4].map(i => <span key={i} style={{ color: '#f59e0b', fontSize: 14 }}>★</span>)}
                <span style={{ color: '#f59e0b', fontSize: 14, opacity: 0.5 }}>★</span>
              </div>
              <span className="text-xs font-semibold text-white">4.9/5</span>
              <span className="text-xs ml-1" style={{ color: 'rgba(255,255,255,0.3)' }}>Negocios reales. Resultados reales.</span>
            </div>
          </div>

          {/* Legal links */}
          <div className="mt-4 pt-4 border-t flex flex-wrap items-center gap-4 justify-center" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
            {[
              { href: '/aviso-legal', label: 'Aviso Legal' },
              { href: '/privacidad', label: 'Privacidad' },
              { href: '/cookies', label: 'Cookies' },
              { href: '/terminos', label: 'Términos' },
            ].map(({ href, label }) => (
              <Link key={href} href={href} className="text-xs transition-opacity hover:opacity-70" style={{ color: 'rgba(255,255,255,0.35)' }}>
                {label}
              </Link>
            ))}
            <CookieSettingsLinkLanding />
          </div>
        </div>
      </footer>
    </div>
  );
}